import { SVGIconType } from '../../constants/svgIcon';
import { isSpaceBounded, TtsLocale } from '../Base';
import { WebBoosterType } from '../UserStore';

import { ConvertType } from './ConvertType';

export enum QuickCreateGameMode {
  Spelling = 'spelling',
  QuickMemory = 'quickMemory',
  HandWriting = 'handWriting',
}

interface QuickCreateGameModeProps {
  isAvailable: boolean;
  name: string;
  description: string;
  icon: SVGIconType;
  color: string;
  languageList: TtsLocale[] | undefined;
  defaultLocale: TtsLocale;
  questionPlaceholder: string;
  answerPlaceholder: string;
  exampleContents: string[];
  hasCopyScriptures: boolean;
  defaultPercentageSelectionThreshold: number;
  defaultRegularSelectionWordCount: number;
  defaultSelectAllThreshold: number;
  defaultQuestionText: string;
  splitLimitation: number;
  convertType: ConvertType[];
}

export const quickCreateGameModeProps: Record<
  QuickCreateGameMode,
  QuickCreateGameModeProps
> = {
  [QuickCreateGameMode.QuickMemory]: {
    isAvailable: true,
    name: 'Knowledge Memory Practice',
    icon: SVGIconType.QuickMemoryJoy,
    color: '#86C81F',
    description:
      'Quick learning of any massive text content for assessment, exam, etc.',
    languageList: undefined,
    defaultLocale: TtsLocale.US,
    questionPlaceholder:
      'Enter question or description e.g. Fill in the blanks.',
    answerPlaceholder:
      'Enter vocabulary, sentences or paragraphs e.g. Creativity is intelligence having fun. Albert Einstein',
    exampleContents: [
      'Creativity is intelligence having fun. Albert Einstein',
      'Imagination is more important than knowledge. Albert Einstein',
    ],
    hasCopyScriptures: false,
    defaultPercentageSelectionThreshold: 0,
    defaultRegularSelectionWordCount: 0,
    defaultSelectAllThreshold: 7,
    defaultQuestionText: 'Fill in the blanks.',
    splitLimitation: 150,
    convertType: [ConvertType.JoyaPassageBasic, ConvertType.JoyaPassage],
  },
  [QuickCreateGameMode.Spelling]: {
    isAvailable: true,
    name: 'Spelling Memory Practice',
    icon: SVGIconType.SpellingJoy,
    color: '#F79302',
    description: 'Accurate spelling practice for dictation, exam, etc.',
    languageList: Object.values(TtsLocale).filter((locale) =>
      isSpaceBounded(locale as TtsLocale),
    ),
    defaultLocale: TtsLocale.US,
    questionPlaceholder:
      'Enter question or description e.g. Fill in the blanks.',
    answerPlaceholder:
      'Enter vocabulary, sentences or paragraphs e.g. Creativity is intelligence having fun. Albert Einstein',
    exampleContents: [
      'Creativity is intelligence having fun. Albert Einstein',
      'Imagination is more important than knowledge. Albert Einstein',
    ],
    hasCopyScriptures: false,
    defaultPercentageSelectionThreshold: 20,
    defaultRegularSelectionWordCount: 6,
    defaultSelectAllThreshold: 5,
    defaultQuestionText: 'Fill in the blanks.',
    splitLimitation: 100,
    convertType: [
      ConvertType.FillInTheBlanksBySyllable,
      ConvertType.FillInTheBlanksBySpelling,
    ],
  },
  [QuickCreateGameMode.HandWriting]: {
    isAvailable: true,
    name: 'Handwriting Memory Practice',
    icon: SVGIconType.WritingJoy,
    color: '#34A3D0',
    description: 'Accurate handwriting practice for dictation, exam, etc.',
    languageList: Object.values(TtsLocale).filter(
      (locale) => !isSpaceBounded(locale as TtsLocale),
    ),
    defaultLocale: TtsLocale.HK,
    questionPlaceholder:
      'Enter question or description e.g. Finish the word with correct stroke order.',
    answerPlaceholder:
      'Enter vocabulary, sentences or paragraphs e.g. Creativity is intelligence having fun. Albert Einstein',
    exampleContents: [
      'Creativity is intelligence having fun. Albert Einstein',
      'Imagination is more important than knowledge. Albert Einstein',
    ],
    hasCopyScriptures: false,
    defaultPercentageSelectionThreshold: 10,
    defaultRegularSelectionWordCount: 4,
    defaultSelectAllThreshold: 4,
    defaultQuestionText: 'Finish the word with correct stroke order.',
    splitLimitation: 50,
    convertType: [
      ConvertType.FingerWritingStrokeByComponent,
      ConvertType.FingerWritingStrokeBySpelling,
    ],
  },
};

export const bibleCreateGameModeProps: Record<
  QuickCreateGameMode,
  QuickCreateGameModeProps
> = {
  [QuickCreateGameMode.QuickMemory]: {
    isAvailable: true,
    name: 'Scripture Memory Practice',
    icon: SVGIconType.QuickMemoryJoy,
    color: '#86C81F',
    description:
      'Quick learning of any massive text content for assessment, exam, etc.',
    languageList: undefined,
    defaultLocale: TtsLocale.US,
    questionPlaceholder: 'Scripture Reference e.g. Proverbs 4:5 (NIV)',
    answerPlaceholder:
      'Input Bible scripture or content for sharing e.g. Proverbs 4:5 (NIV)',
    exampleContents: [
      'Proverbs 4:5 (NIV) Get wisdom, get understanding; do not forget my words or turn away from them.',
      'Deuteronomy 11:18 (NIV) Fix these words of mine in your hearts and minds; tie them as symbols on your hands and bind them on your foreheads.',
    ],
    hasCopyScriptures: true,
    defaultPercentageSelectionThreshold: 0,
    defaultRegularSelectionWordCount: 0,
    defaultSelectAllThreshold: 7,
    defaultQuestionText: 'Fill in the blanks.',
    splitLimitation: 150,
    convertType: [ConvertType.ScriptureBasic, ConvertType.ScriptureStandard],
  },
  [QuickCreateGameMode.Spelling]: {
    isAvailable: true,
    name: 'Spelling Memory Practice',
    icon: SVGIconType.SpellingJoy,
    color: '#F79302',
    description: 'Accurate spelling practice for dictation, exam, etc.',
    languageList: Object.values(TtsLocale).filter((locale) =>
      isSpaceBounded(locale as TtsLocale),
    ),
    defaultLocale: TtsLocale.US,
    questionPlaceholder: 'Scripture Reference e.g. Proverbs 4:5 (NIV)',
    answerPlaceholder:
      'Input Bible scripture or content for sharing e.g. Proverbs 4:5 (NIV)',
    exampleContents: [
      'Proverbs 4:5 (NIV) Get wisdom, get understanding; do not forget my words or turn away from them.',
      'Deuteronomy 11:18 (NIV) Fix these words of mine in your hearts and minds; tie them as symbols on your hands and bind them on your foreheads.',
    ],
    hasCopyScriptures: true,
    defaultPercentageSelectionThreshold: 20,
    defaultRegularSelectionWordCount: 6,
    defaultSelectAllThreshold: 5,
    defaultQuestionText: 'Fill in the blanks.',
    splitLimitation: 100,
    convertType: [
      ConvertType.FillInTheBlanksBySyllable,
      ConvertType.FillInTheBlanksBySpelling,
    ],
  },
  [QuickCreateGameMode.HandWriting]: {
    isAvailable: true,
    name: 'Handwriting Memory Practice',
    icon: SVGIconType.WritingJoy,
    color: '#34A3D0',
    description: 'Accurate handwriting practice for dictation, exam, etc.',
    languageList: Object.values(TtsLocale).filter(
      (locale) => !isSpaceBounded(locale as TtsLocale),
    ),
    defaultLocale: TtsLocale.HK,
    questionPlaceholder: 'Scripture Reference e.g. Proverbs 4:5 (NIV)',
    answerPlaceholder:
      'Input Bible scripture or content for sharing e.g. Proverbs 4:5 (NIV)',
    exampleContents: [
      'Proverbs 4:5 (NIV) Get wisdom, get understanding; do not forget my words or turn away from them.',
      'Deuteronomy 11:18 (NIV) Fix these words of mine in your hearts and minds; tie them as symbols on your hands and bind them on your foreheads.',
    ],
    hasCopyScriptures: true,
    defaultPercentageSelectionThreshold: 10,
    defaultRegularSelectionWordCount: 4,
    defaultSelectAllThreshold: 4,
    defaultQuestionText: 'Finish the word with correct stroke order.',
    splitLimitation: 50,
    convertType: [
      ConvertType.FingerWritingStrokeByComponent,
      ConvertType.FingerWritingStrokeBySpelling,
    ],
  },
};

export const getQuickCreateGameModeVersion = (
  webBoosterType: WebBoosterType | undefined,
) => {
  if (webBoosterType === WebBoosterType.Bible) {
    return bibleCreateGameModeProps;
  }
  return quickCreateGameModeProps;
};

export const getQuickCreateGameModeProps = (
  mode: QuickCreateGameMode,
  webBoosterType: WebBoosterType | undefined,
) => {
  return getQuickCreateGameModeVersion(webBoosterType)[mode];
};
